// File: src/components/ModalRegisterOptions.jsx
// Estratégia: usar React Portal + overlay fixo (z-index alto) e posicionamento por getBoundingClientRect.
// Evita problemas de z-index/stacking-context e mantém o menu ancorado ao botão, com clamp nas bordas.

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

export default function ModalRegisterOptions({
  isOpen,
  onClose,
  buttonRef,
  canCreateAttendant = false,
  canCreateContact = false,
  canCreateCompany = false,
}) {
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const computePosition = useCallback(() => {
    const btn = buttonRef?.current;
    const menu = menuRef.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 8;

    if (!btn) {
      setPos({ top: Math.max(8, vh / 2 - 100), left: Math.max(8, vw / 2 - 120) });
      return;
    }

    const rect = btn.getBoundingClientRect();
    const menuW = menu?.offsetWidth ?? 200;
    const menuH = menu?.offsetHeight ?? 120;

    let top = rect.bottom + gap; // abaixo do botão por padrão
    let left = rect.left;

    if (left + menuW > vw - 8) left = Math.max(8, vw - menuW - 8); // clamp horizontal
    if (top + menuH > vh - 8) top = Math.max(8, rect.top - menuH - gap); // se não couber abaixo, coloca acima

    setPos({ top, left });
  }, [buttonRef]);

  useEffect(() => {
    if (!isOpen) return;
    computePosition();

    const onScroll = () => computePosition(); // fecha/bloqueia se necessário? aqui apenas reposiciona
    const onResize = () => computePosition();

    window.addEventListener('scroll', onScroll, true); // true captura scroll em containers
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen, computePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose(); // fecha ao clicar fora
      }}
      aria-hidden={false}
    >
      <div
        ref={menuRef}
        className="modal-register-options"
        role="dialog"
        aria-modal="true"
        style={{ position: 'absolute', top: pos.top, left: pos.left }}
        onMouseDown={(e) => e.stopPropagation()} // evita fechar ao clicar dentro
      >
        <div className="modal-register-content">
          {canCreateAttendant && (
            <button onClick={() => { navigate('/attendants', { state: { openCreate: true } }); onClose(); }}>
              Atendente
            </button>
          )}
          {canCreateContact && (
            <button onClick={() => { navigate('/contacts', { state: { openCreate: true } }); onClose(); }}>
              Contato
            </button>
          )}
          {canCreateCompany && (
            <button onClick={() => { navigate('/companyRegister'); onClose(); }}>
              Empresa
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
